package org.braunequip.web.styles

import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.functions.min
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.forms.ButtonStyle
import com.varabyte.kobweb.silk.components.forms.ButtonVars
import com.varabyte.kobweb.silk.components.layout.HorizontalDividerStyle
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.init.registerStyleBase
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.addVariantBase
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.layer.SilkLayer
import com.varabyte.kobweb.silk.style.layer.add
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import com.varabyte.kobweb.silk.theme.modifyStyleBase
import org.braunequip.web.util.Constants.FONT_FAMILY
import org.jetbrains.compose.web.css.*


/*
    Default Kobweb Styles
*/
@InitSilk
fun initSiteStyles(ctx: InitSilkContext) {
    // This site does not need scrolling itself, but this is a good demonstration for how you might enable this in your
    // own site. Note that we only enable smooth scrolling unless the user has requested reduced motion, which is
    // considered a best practice.
    ctx.stylesheet.registerStyle("html") {
        cssRule(CSSMediaQuery.MediaFeature("prefers-reduced-motion", StylePropertyValue("no-preference"))) {
            Modifier.scrollBehavior(ScrollBehavior.Smooth)
        }
    }

    ctx.stylesheet.registerStyleBase("body") {
        Modifier
            .fontFamily(FONT_FAMILY)
            .fontSize(18.px)
            .lineHeight(1.5)
    }

    // Silk dividers only extend 90% by default; we want full width dividers in our site
    ctx.theme.modifyStyleBase(HorizontalDividerStyle) {
        Modifier.fillMaxWidth()
    }

    ctx.stylesheet.cssLayers.add("bulmacss", after = SilkLayer.BASE)

}

val HeadlineTextStyle = CssStyle {
    base {
        Modifier
            .fontSize(2.5.cssRem)
            .textAlign(TextAlign.Start)
            .lineHeight(1.2) //1.5x doesn't look as good on very large text
    }
    Breakpoint.MD {
        Modifier
            .fontSize(3.3.cssRem)

    }
}

val SubheadlineTextStyle = CssStyle.base {
    Modifier
        .fontSize(1.7.cssRem)
        .textAlign(TextAlign.Start)
        .color(colorMode.toPalette().color.toRgb().copyf(alpha = 0.8f))
}

val CircleButtonVariant = ButtonStyle.addVariantBase {
    Modifier.padding(0.px).borderRadius(50.percent)
}

val UncoloredButtonVariant = ButtonStyle.addVariantBase {
    Modifier.setVariable(ButtonVars.BackgroundDefaultColor, Colors.Transparent)
}

/*
    Owen Added Global Styles
    - Styles I want to keep broad and reusable.
*/
fun Modifier.noBorder(): Modifier {
    return this
        .border (
            width = 0.px,
            style = LineStyle.None,
            color = Colors.Transparent
        )
        .outline(
            width = 0.px,
            style = LineStyle.None,
            color = Colors.Transparent
        )
}

val centeredSection = CssStyle {
    base {
        Modifier
            .margin(leftRight = autoLength)
    }
}

val cappedWidth = CssStyle {
    base {
        Modifier
            .margin(leftRight = autoLength)
            .width(min(900.px, 100.percent - 1.cssRem))
    }
    Breakpoint.MD {
        Modifier
            .width(min(1500.px, 100.percent - 1.cssRem))
    }
}

val RespSplitContainer = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
            .alignItems(org.jetbrains.compose.web.css.AlignItems.Center)
    }
    Breakpoint.MD {
        Modifier
            .flexDirection(FlexDirection.Row)
            .alignItems(org.jetbrains.compose.web.css.AlignItems.Start)
    }
}

val RespButtonStyle = CssStyle {
    base {
        Modifier
            .transition(Transition.of("ease-in-out"), Transition.of("all", 0.2.s))
    }
    hover {
        Modifier
            .cursor(Cursor.Pointer)
            .scale(1.1)
    }
}

/*
    GENERAL PAGE STYLES/CONTAINERS
*/
val PageHeroContainer = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .gap(2.cssRem)
    }
    Breakpoint.MD {
        Modifier
    }
}

val PageHeroSectionRow = CssStyle {
    base {
        Modifier
            .padding(top = 2.cssRem, left = 1.cssRem, bottom = 4.cssRem)
    }
    Breakpoint.MD {
        Modifier
            .padding(top = 8.cssRem, left = 15.cssRem, bottom = 12.cssRem)
    }
}

val PageHeroHeadlineStyle = CssStyle {
    base {
        Modifier
            .fontSize(2.8.cssRem)
            .fontWeight(FontWeight.Bold)
            .textAlign(TextAlign.Start)
            .lineHeight(1.2) //1.5x doesn't look as good on very large text
            .color(Colors.White)
    }
    Breakpoint.MD {
        Modifier
            .fontSize(5.cssRem)
            .color(Colors.White)
            .textTransform(TextTransform.Uppercase)
    }
}

/*
    SECTION STYLING
*/
val SectionContainer = CssStyle {
    base {
        Modifier
            .padding(topBottom = 1.5.cssRem)
    }
    Breakpoint.MD {
        Modifier
            .padding(topBottom = 3.cssRem)
    }
}

val SectionTitleRow = CssStyle {
    base {
        Modifier
            .padding(left = 2.cssRem)
    }
    Breakpoint.MD {
        Modifier
            .padding(left = 2.cssRem)
    }
}

val SectionSubtitleRow = CssStyle {
    base {
        Modifier
            .padding(topBottom = 2.cssRem, leftRight = 2.cssRem)
            .color(Colors.White)
            .fontSize(16.px)
    }
    Breakpoint.MD {
        Modifier
            .fontSize(28.px)
    }
}
